// Deck data model for Riftbound TCG deck builder.

import { currentMetaPeriod } from '../../shared/metaPeriods.js';
import { CHOSEN_LEGEND_COUNT } from '../../shared/chosenLegends.js';

export interface DeckCardRef {
  cardId: string;
  name: string;
  qty: number;
  setCode?: string;
  setName?: string;
  number?: string;
  riftboundId?: string;
  /** Parent product set for a promo printing (OPP/PR/JDG). Rift Atlas uses this code. */
  originSet?: string;
  imageUrl?: string;
  thumbnailUrl?: string;
  cardType?: string;
  supertype?: string;
  orientation?: 'portrait' | 'landscape';
  cost?: number;
  might?: number;
  power?: number;
  domain?: string[];
  champion?: string;
  /** Riftcodex tags — used to match legends/champions/signature cards by champion identity. */
  tags?: string[];
  /** Signed/star Legend print — not the playable Signature-supertype spell. */
  signature?: boolean;
  /**
   * Catalog rarity; drives the foil finish on deck thumbs. Absent on cards
   * added before this field existed; `DeckCardThumb` looks those up.
   */
  rarity?: string;
}

export interface DeckRune {
  domain: string;
  qty: number;
  /**
   * Chosen printing. Absent means the basic domain rune. Quantity changes
   * keep this so a showcase or alt art survives the stepper and rune split.
   */
  print?: {
    cardId: string;
    name: string;
    setCode?: string;
    number?: string;
    riftboundId?: string;
    /** Parent product set when `setCode` is a promo catalog (OPP/PR/JDG). */
    originSet?: string;
    imageUrl?: string;
    thumbnailUrl?: string;
  };
}

export interface DeckMatchup {
  archetype: string;
  result: 'favored' | 'even' | 'unfavored';
  notes: string;
}

export interface DeckVideo {
  url: string;
  title?: string;
}

/** Linked Metafy guide/article on the deck Guide tab. */
export interface DeckMetafyArticle {
  url: string;
  title?: string;
  coverUrl?: string;
  /** USD cents from Metafy’s API. `0` is free; omit when unknown. */
  priceCents?: number;
  /** Always USD when present. */
  currency?: string;
  subscriberOnly?: boolean;
}

export type DeckPlaystyle = 'aggro' | 'midrange' | 'control' | 'combo' | 'tempo';
/**
 * Deck-builder format codes. Constructed expansions (OGN/SFD/UNL/VEN) share
 * codes with meta periods in `shared/metaPeriods.js`; OGS is a starter product
 * and does not start a meta. `formatCode` is a label for the gallery / publish
 * UI — deck construction validation does not currently gate cards by meta.
 */
export type DeckFormat = 'VEN' | 'UNL' | 'OGN' | 'SFD' | 'OGS';
export type DeckStatus = 'draft' | 'published';
export type DeckVisibility = 'private' | 'public' | 'team';

/** Mutable deck state used in the builder. */
export interface DeckDraft {
  title: string;
  description: string;
  guide: string;
  video?: DeckVideo;
  metafy?: DeckMetafyArticle;
  playstyle: DeckPlaystyle[];
  formatCode: DeckFormat;
  tags: string[];
  coverCardId?: string;
  matchups: DeckMatchup[];
  legend?: DeckCardRef;
  /**
   * Neeko, Blending In: three legends besides the starting legend. Empty unless
   * she is in the deck. They never change domains, runes, or the champion.
   */
  chosenLegends: DeckCardRef[];
  champion?: DeckCardRef;
  battlefields: DeckCardRef[];
  runes: DeckRune[];
  mainDeck: DeckCardRef[];
  sideboard: DeckCardRef[];
  /**
   * Owner-only scratch pile (edit/builder). Unlimited size; excluded from copy
   * limits and legality. Hidden from public deck views and cleared on remix.
   */
  considered: DeckCardRef[];
  status: DeckStatus;
  visibility: DeckVisibility;
}

/** List piles that accept regular deck cards via add / move / DnD. */
export type DeckListZone = 'mainDeck' | 'sideboard' | 'considered';

export interface DeckTournamentResult {
  topdeckTournamentId: string;
  eventName: string;
  placement: number;
  playerCount: number;
  date: string;
  topdeckUrl: string;
}

/** Persisted deck document from Firestore. */
export interface Deck extends DeckDraft {
  id: string;
  ownerId: string;
  ownerDisplayName: string;
  ownerHandle: string;
  /**
   * Author avatar, denormalized at save time so the gallery does not read one
   * `publicDirectory` document per distinct author. Absent on decks last saved
   * before this field existed; the UI falls back to initials.
   */
  ownerAvatarUrl?: string | null;
  /** Set when visibility is 'team'; cleared when leaving the team. */
  teamId?: string | null;
  isValid: boolean;
  validationErrors: string[];
  likeCount: number;
  viewCount: number;
  currentVersion: number;
  createdAt: number;
  updatedAt: number;
  tournamentResult?: DeckTournamentResult;
}

/** Version snapshot stored in decks/{id}/versions/{n}. */
export interface DeckVersion {
  versionNumber: number;
  changeNote?: string;
  snapshot: DeckDraft & { isValid: boolean; validationErrors: string[] };
  savedAt: number;
  savedBy: string;
}

/** Zone a construction issue belongs to (for inline coaching in the builder). */
export type DeckValidationZone =
  | 'legend'
  | 'chosenLegends'
  | 'champion'
  | 'battlefield'
  | 'runes'
  | 'mainDeck'
  | 'sideboard'
  | 'general';

export interface DeckValidationIssue {
  zone: DeckValidationZone;
  message: string;
}

export interface ValidationResult {
  isValid: boolean;
  /** Flat messages for Firestore / legacy UI (derived from `issues`). */
  errors: string[];
  warnings: string[];
  /** Structured issues for section-level coaching. */
  issues: DeckValidationIssue[];
}

/**
 * Authoritative Riftbound constructed deck limits.
 *
 * Key rule: MAIN_DECK_SIZE (40) *includes* the Chosen Champion.
 * The champion is placed in the Champion Zone at game start but it occupies
 * CHAMPION_DECK_SLOTS (1) of the 40 slots.
 *
 * Use mainDeckCount() for the rule-correct 40-card total (champion + library).
 * Use libraryCount() only when you need the draw-pile size without the champion
 * (e.g. opening-hand simulators that draw from the shuffled library).
 *
 * Server-side mirror lives in api/_shared/deckConstants.js — keep DECK_LIMITS in sync.
 * Per-user deck/binder quotas live in src/constants/userLimits.ts (USER_LIMITS).
 */
export const DECK_LIMITS = {
  /** Total main deck size including the Chosen Champion. */
  MAIN_DECK_SIZE: 40,
  /** Slots the Chosen Champion occupies within MAIN_DECK_SIZE. */
  CHAMPION_DECK_SLOTS: 1,
  BATTLEFIELD_COUNT: 3,
  /** Extra legends Neeko, Blending In requires. Rule lives in `shared/chosenLegends.js`. */
  CHOSEN_LEGEND_COUNT,
  RUNE_COUNT: 12,
  /** Maximum total cards in the optional sideboard (0–10 inclusive). */
  SIDEBOARD_MAX: 10,
  /**
   * Default max copies of any named card across champion + main + sideboard.
   * Per-card exceptions (e.g. Spiderling) live in `constants/deckCopyRules.ts`
   * and are resolved via `utils/deckCopyLimits.ts`.
   */
  MAX_COPIES: 3,
} as const;

export const DECK_FORMAT_LABELS: Record<DeckFormat, string> = {
  VEN: 'Vendetta',
  UNL: 'Unleashed',
  OGN: 'Origins',
  SFD: 'Spiritforged',
  OGS: 'Origins: Proving Grounds',
};

function isDeckFormat(code: string | undefined): code is DeckFormat {
  return !!code && Object.prototype.hasOwnProperty.call(DECK_FORMAT_LABELS, code);
}

/** Newest released format the builder can label. Upcoming sets stay off until they are in the list. */
export function defaultDeckFormat(today?: string): DeckFormat {
  const current = currentMetaPeriod(today)?.code;
  if (isDeckFormat(current)) return current;
  const [newest] = Object.keys(DECK_FORMAT_LABELS) as DeckFormat[];
  return newest;
}

export const DECK_PLAYSTYLE_LABELS: Record<DeckPlaystyle, string> = {
  aggro: 'Aggro',
  midrange: 'Midrange',
  control: 'Control',
  combo: 'Combo',
  tempo: 'Tempo',
};

/** Passed via react-router `location.state` when opening `/decks/new`. */
export interface DeckBuilderLocationState {
  remixedDraft?: DeckDraft;
  /** Pre-select legend when starting from a legend card (e.g. modal “Build a … deck”). */
  presetLegend?: DeckCardRef;
}

export function emptyDeckDraft(): DeckDraft {
  return {
    title: '',
    description: '',
    guide: '',
    video: undefined,
    metafy: undefined,
    playstyle: [],
    formatCode: defaultDeckFormat(),
    tags: [],
    coverCardId: undefined,
    matchups: [],
    legend: undefined,
    chosenLegends: [],
    champion: undefined,
    battlefields: [],
    runes: [],
    mainDeck: [],
    sideboard: [],
    considered: [],
    status: 'draft',
    visibility: 'private',
  };
}

/**
 * Rule-correct total for the main deck (champion zone + library), checked
 * against DECK_LIMITS.MAIN_DECK_SIZE (40). Use this for publish validation and
 * user-facing "X / 40" displays. Draft editing does not hard-cap main size —
 * only copy limits and the sideboard max are enforced while building.
 */
export function mainDeckCount(draft: DeckDraft): number {
  return libraryCount(draft) + (draft.champion ? DECK_LIMITS.CHAMPION_DECK_SLOTS : 0);
}

/**
 * Copies of the 40-card deck the viewer owns: library quantities capped at
 * each line's qty, plus one if they own the chosen champion.
 */
export function ownedMainDeckCount(
  draft: Pick<DeckDraft, 'mainDeck' | 'champion'>,
  ownedQty: (cardId: string) => number,
): number {
  const library = draft.mainDeck.reduce(
    (sum, card) => sum + Math.min(Math.max(0, ownedQty(card.cardId) || 0), card.qty),
    0,
  );
  if (!draft.champion) return library;
  const haveChampion = Math.max(0, ownedQty(draft.champion.cardId) || 0) > 0;
  return library + (haveChampion ? DECK_LIMITS.CHAMPION_DECK_SLOTS : 0);
}

/** Owned copies of the 40-card deck, or undefined when the deck has no cards to own. */
export function ownedMainDeckFraction(
  draft: Pick<DeckDraft, 'mainDeck' | 'champion'>,
  ownedQty: (cardId: string) => number,
): { owned: number; total: number } | undefined {
  if (draft.mainDeck.length === 0 && !draft.champion) return undefined;
  return {
    owned: ownedMainDeckCount(draft, ownedQty),
    total: DECK_LIMITS.MAIN_DECK_SIZE,
  };
}

/** Card lines that roll up into the deck's market value. Same set as deck details. */
export function deckMarketValueRefs(
  draft: Pick<DeckDraft, 'legend' | 'champion' | 'battlefields' | 'mainDeck' | 'sideboard'> &
    Partial<Pick<DeckDraft, 'chosenLegends'>>,
): DeckCardRef[] {
  return [
    ...(draft.legend ? [draft.legend] : []),
    ...(draft.chosenLegends ?? []),
    ...(draft.champion ? [draft.champion] : []),
    ...draft.battlefields,
    ...draft.mainDeck,
    ...draft.sideboard,
  ];
}

/**
 * Number of cards in the shuffled library (mainDeck array only, champion
 * excluded). Use this wherever you need the draw-pile size — e.g. opening-hand
 * simulators and probability calculations that draw from the library, not the
 * Champion Zone.
 */
export function libraryCount(draft: DeckDraft): number {
  return draft.mainDeck.reduce((s, c) => s + c.qty, 0);
}

/** Total sideboard card count. */
export function sideboardCount(draft: DeckDraft): number {
  return draft.sideboard.reduce((s, c) => s + c.qty, 0);
}

/** Total considered (scratch) card count. */
export function consideredCount(draft: DeckDraft): number {
  return draft.considered.reduce((s, c) => s + c.qty, 0);
}

/**
 * Strip owner-private fields (e.g. considered) for non-owner viewers.
 * Remix callers should clear considered separately via {@link clearConsideredForRemix}.
 */
export function sanitizeDraftForViewer<T extends DeckDraft>(
  draft: T,
  opts: { isOwner: boolean },
): T {
  if (opts.isOwner) return draft;
  if (draft.considered.length === 0) return draft;
  return { ...draft, considered: [] };
}

/** Remix always starts with an empty considered pile. */
export function clearConsideredForRemix<T extends DeckDraft>(draft: T): T {
  if (draft.considered.length === 0) return draft;
  return { ...draft, considered: [] };
}

/** Total rune count. */
export function runeCount(draft: DeckDraft): number {
  return draft.runes.reduce((s, r) => s + r.qty, 0);
}

/** Domains available from the legend. */
export function legendDomains(draft: DeckDraft): string[] {
  return draft.legend?.domain ?? [];
}
